import { Plus, Trash2 } from "lucide-react";
import type { Rule, RuleAction, RuleSet, RuleTrigger } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

const TRIGGERS: RuleTrigger[] = ["ENTRY_FILLED", "TP1_HIT", "TP2_HIT", "TP3_HIT", "SL_HIT", "SIGNAL_CLOSED"];

const ACTION_TYPES = [
  "MOVE_SL_TO_BE",
  "MOVE_SL_TO_TP",
  "MOVE_SL_TO_R",
  "CLOSE_REMAINING",
  "PARTIAL_CLOSE",
  "SET_TRAILING",
  "CANCEL_PENDING",
] as const;

export function defaultAction(type: (typeof ACTION_TYPES)[number]): RuleAction {
  switch (type) {
    case "MOVE_SL_TO_BE":
      return { type, offsetPips: 0, bufferSpreads: 2 };
    case "MOVE_SL_TO_TP":
      return { type, tpSlot: 1 };
    case "MOVE_SL_TO_R":
      return { type, rMultiple: -0.5 };
    case "CLOSE_REMAINING":
      return { type };
    case "PARTIAL_CLOSE":
      return { type, pct: 50 };
    case "SET_TRAILING":
      return { type, distancePips: 20 };
    case "CANCEL_PENDING":
      return { type };
  }
}

export function parseSlots(text: string): number[] | undefined {
  const nums = text
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 3);
  return nums.length === 0 ? undefined : nums;
}

export function RuleSetEditor({ value, onChange }: { value: RuleSet; onChange: (next: RuleSet) => void }) {
  const setRule = (index: number, rule: Rule) => onChange(value.map((r, i) => (i === index ? rule : r)));
  const removeRule = (index: number) => onChange(value.filter((_, i) => i !== index));
  const addRule = () => onChange([...value, { trigger: "TP1_HIT", actions: [] }]);

  return (
    <div className="space-y-3">
      {value.length === 0 && <p className="text-sm text-muted-foreground">No rules configured.</p>}

      {value.map((rule, ruleIndex) => (
        <Card key={ruleIndex}>
          <CardContent className="space-y-3 p-3">
            <div className="flex items-center justify-between gap-2">
              <Select
                value={rule.trigger}
                onChange={(e) => setRule(ruleIndex, { ...rule, trigger: e.target.value as RuleTrigger })}
                className="max-w-[220px]"
              >
                {TRIGGERS.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="ghost" onClick={() => removeRule(ruleIndex)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2 border-l-2 border-border pl-3">
              {rule.actions.map((action, actionIndex) => (
                <ActionEditor
                  key={actionIndex}
                  action={action}
                  onChange={(next) =>
                    setRule(ruleIndex, {
                      ...rule,
                      actions: rule.actions.map((a, i) => (i === actionIndex ? next : a)),
                    })
                  }
                  onRemove={() =>
                    setRule(ruleIndex, { ...rule, actions: rule.actions.filter((_, i) => i !== actionIndex) })
                  }
                />
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRule(ruleIndex, { ...rule, actions: [...rule.actions, defaultAction("MOVE_SL_TO_BE")] })}
              >
                <Plus className="h-3.5 w-3.5" /> Add action
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <Button variant="outline" className="w-full" onClick={addRule}>
        <Plus className="h-4 w-4" /> Add rule
      </Button>
    </div>
  );
}

function ActionEditor({
  action,
  onChange,
  onRemove,
}: {
  action: RuleAction;
  onChange: (next: RuleAction) => void;
  onRemove: () => void;
}) {
  const slotsText = action.slots?.join(", ") ?? "";

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <div className="flex items-center justify-between gap-2">
        <Select
          value={action.type}
          onChange={(e) => onChange(defaultAction(e.target.value as (typeof ACTION_TYPES)[number]))}
          className="max-w-[220px]"
        >
          {ACTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, " ")}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {action.type === "MOVE_SL_TO_BE" && (
        <div className="grid grid-cols-2 gap-2">
          <LabeledNumber
            label="Offset (pips)"
            value={action.offsetPips ?? 0}
            onChange={(n) => onChange({ ...action, offsetPips: n })}
          />
          <LabeledNumber
            label="Buffer (× spread)"
            // A rule saved before bufferSpreads existed has no value here — the server upgrades
            // it to 2 wherever the engine reads it (ruleSetSchema's own default), but this GET
            // returns the raw stored row, so the editor defaults it the same way for display.
            value={action.bufferSpreads ?? 2}
            onChange={(n) => onChange({ ...action, bufferSpreads: n })}
          />
        </div>
      )}

      {action.type === "MOVE_SL_TO_TP" && (
        <LabeledNumber label="Trail to TP slot" value={action.tpSlot} onChange={(n) => onChange({ ...action, tpSlot: n })} />
      )}

      {action.type === "MOVE_SL_TO_R" && (
        <LabeledNumber
          label="Stop at (R)"
          value={action.rMultiple}
          onChange={(n) => onChange({ ...action, rMultiple: n })}
        />
      )}

      {action.type === "PARTIAL_CLOSE" && (
        <LabeledNumber label="Percent" value={action.pct} onChange={(n) => onChange({ ...action, pct: n })} />
      )}

      {action.type === "SET_TRAILING" && (
        <LabeledNumber
          label="Distance (pips)"
          value={action.distancePips}
          onChange={(n) => onChange({ ...action, distancePips: n })}
        />
      )}

      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Slots (1-3, blank = all)</label>
        <Input
          value={slotsText}
          onChange={(e) => onChange({ ...action, slots: parseSlots(e.target.value) })}
          placeholder="e.g. 2, 3"
        />
      </div>
    </div>
  );
}

function LabeledNumber({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      <Input
        type="number"
        step="any"
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </div>
  );
}
